import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { CloseButton, FontText, PrimaryButton, BlueButton } from './common';
import {
  SubscriptionPlan,
  coinOptions,
  createPaywallStyles,
  CoinOption,
  FeatureList,
  GuaranteeBadge,
  CoinOptionItem,
  subscriptionFeatures,
} from './common/PaywallShared';

import { localAnalytics } from '../utils/analytics';
import { logErrorsWithMessage } from '../utils/errors';
import { useAuth } from '../contexts/AuthContext';
import { usePurchases } from '../contexts/PurchasesContext';
import { fbTracking } from '../utils/fbTracking';

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseSuccess: () => void;
  bookTitle: string;
  chapterNumber: number;
}

const subscriptionPlans: SubscriptionPlan[] = [
  {
    id: 'subscriptionyearly80',
    price: '79.99',
    displayPrice: '$79.99',
    period: 'Yearly',
    type: 'yearly',
  },
  {
    id: 'subscriptionweekly',
    price: '8.99',
    displayPrice: '$8.99',
    period: 'Weekly',
    type: 'weekly',
  },
];

export const PaywallModal: React.FC<PaywallModalProps> = ({
  isOpen,
  onClose,
  onPurchaseSuccess,
  bookTitle,
  chapterNumber,
}) => {
  const { t } = useTranslation();
  const { theme, themeMode } = useTheme();
  const { userId } = useAuth();
  const { purchaseCoins, purchaseSubscription } = usePurchases();
  const [selectedOption, setSelectedOption] = useState<CoinOption>(
    coinOptions[2]
  );
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan>(
    subscriptionPlans[0]
  );
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'coins' | 'subscription'>(
    'subscription'
  );
  const [error, setError] = useState<string | null>(null);

  const styles = createPaywallStyles(theme, themeMode);

  const handlePurchase = async (productId?: string, price?: string) => {
    const finalProductId =
      productId ||
      (activeTab === 'subscription' ? selectedPlan.id : selectedOption.id);
    const finalPrice =
      price ||
      (activeTab === 'subscription'
        ? selectedPlan.price
        : selectedOption.price);

    setIsLoading(true);
    setError(null);

    try {
      localAnalytics().logEvent('ChapterPurchaseInitiated', {
        screen: 'Chapter',
        action: 'PurchaseInitiated',
        userId,
        productId: finalProductId,
        price: finalPrice,
        bookTitle,
        chapterNumber,
        type: activeTab === 'subscription' ? 'subscription' : 'coins',
      });

      fbTracking.trackInitiateCheckout(bookTitle, finalPrice, false);

      const result =
        activeTab === 'subscription'
          ? await purchaseSubscription(finalProductId)
          : await purchaseCoins(finalProductId);

      if (!result.success) {
        if (result.error) {
          throw new Error(result.error);
        } else {
          return;
        }
      }

      localAnalytics().logEvent('ChapterPurchaseSuccess', {
        screen: 'Chapter',
        action: 'PurchaseSuccess',
        userId,
        productId: finalProductId,
        price: finalPrice,
        bookTitle,
        chapterNumber,
      });

      fbTracking.trackPurchase(bookTitle, finalProductId, finalPrice, false);

      onPurchaseSuccess();
    } catch (error: any) {
      console.error('Purchase failed:', error);
      setError(error.message || 'Purchase failed. Please try again.');
      logErrorsWithMessage(error, 'Purchase failed');
      localAnalytics().logEvent('ChapterPurchaseError', {
        screen: 'Chapter',
        action: 'PurchaseError',
        userId,
        error: error instanceof Error ? error.message : 'Unknown error',
        productId: finalProductId,
        bookTitle,
        chapterNumber,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCoinOptionClick = async (option: CoinOption) => {
    setSelectedOption(option);
    await handlePurchase(option.id, option.price);
  };

  const handleSubscriptionPurchase = async () => {
    await handlePurchase(selectedPlan.id, selectedPlan.price);
  };

  const handlePlanSelection = async (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    localAnalytics().logEvent('SubscriptionPlanSelected', {
      screen: 'Chapter',
      action: 'PlanSelected',
      userId,
      planId: plan.id,
      planType: plan.type,
      price: plan.price,
      bookTitle,
      chapterNumber,
    });
    await handlePurchase(plan.id, plan.price);
  };

  const handleTabChange = (tab: 'coins' | 'subscription') => {
    localAnalytics().logEvent('PaywallModalTabChanged', {
      screen: 'Chapter',
      action: 'TabChanged',
      userId,
      tab,
      bookTitle,
      chapterNumber,
    });
    setActiveTab(tab);
    setError(null);
  };

  const handleCloseClick = () => {
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <img
            src="/img/favicon.png"
            alt={t('app_name')}
            style={styles.appIcon}
          />
          <FontText style={styles.appName}>{t('app_name')}</FontText>
        </div>
        <CloseButton
          onClose={handleCloseClick}
          analyticsEvent="PaywallModalClosed"
          themeMode={themeMode}
        />
      </div>

      <div style={styles.content}>
        <div style={styles.card}>
          <div style={styles.tabs}>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === 'subscription' ? styles.activeTab : {}),
              }}
              onClick={() => handleTabChange('subscription')}
              disabled={isLoading}
            >
              <FontText small>{t('unlimited_reading')}</FontText>
            </button>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === 'coins' ? styles.activeTab : {}),
              }}
              onClick={() => handleTabChange('coins')}
              disabled={isLoading}
            >
              <FontText small>{t('buy_chapters')}</FontText>
            </button>
          </div>

          {error && (
            <div style={styles.errorContainer}>
              <div style={styles.errorText}>
                <FontText small style={{ color: '#991b1b' }}>
                  {error}
                </FontText>
              </div>
            </div>
          )}

          {activeTab === 'subscription' ? (
            <>
              <img
                src="/img/books/paywall_subscription.png"
                alt="Subscription"
                style={styles.subscriptionImage}
              />
              <FontText h1 style={styles.title}>
                {t('subscribe_unlimited_access')}
              </FontText>
              <FeatureList
                features={subscriptionFeatures}
                t={t}
                theme={theme}
                featureTextStyle={styles.featureText}
              />
              <div style={styles.subscriptionPlans}>
                {subscriptionPlans.map(plan => (
                  <div
                    key={plan.id}
                    style={{
                      ...styles.subscriptionPlan,
                      ...(selectedPlan.id === plan.id
                        ? styles.selectedPlan
                        : {}),
                    }}
                    onClick={() => handlePlanSelection(plan)}
                  >
                    <div style={styles.planContent}>
                      <FontText style={styles.planPrice}>
                        {plan.displayPrice}
                      </FontText>
                      <FontText style={styles.planPeriod}>
                        {t(plan.type)}
                      </FontText>
                    </div>
                    <div
                      style={{
                        ...styles.planCheck,
                        ...(selectedPlan.id === plan.id
                          ? {
                              backgroundColor:
                                themeMode === 'dark'
                                  ? theme.colors.text
                                  : '#ffffff',
                              border: 'none',
                            }
                          : {}),
                      }}
                    >
                      {selectedPlan.id === plan.id && (
                        <img
                          src="/img/books/selected_subscription.png"
                          width="13"
                          height="13"
                          alt="check"
                        />
                      )}
                    </div>
                    {plan.type === 'yearly' && (
                      <div style={styles.discountBadge}>
                        <FontText small style={styles.discountText}>
                          -91%
                        </FontText>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <GuaranteeBadge
                t={t}
                theme={theme}
                guaranteeIconStyle={styles.guaranteeIcon}
                guaranteeTextStyle={styles.guaranteeText}
              />
            </>
          ) : (
            <>
              <FontText h1 style={styles.title}>
                {t('buy_coins_unlock')}
              </FontText>
              <FontText style={styles.subtitle}>
                {t('coins_per_chapter')}
              </FontText>
              <div style={styles.coinOptions}>
                {coinOptions.map(option => (
                  <CoinOptionItem
                    key={option.id}
                    option={option}
                    isLoading={isLoading}
                    onClick={handleCoinOptionClick}
                    t={t}
                    theme={theme}
                    themeMode={themeMode}
                  />
                ))}
              </div>
              <FontText tiny style={styles.coinsNote}>
                {t('coins_dont_expire')}
              </FontText>
              <GuaranteeBadge
                t={t}
                theme={theme}
                guaranteeIconStyle={styles.guaranteeIcon}
                guaranteeTextStyle={styles.guaranteeText}
              />
            </>
          )}

          {themeMode === 'light' ? (
            <BlueButton
              onClick={
                activeTab === 'subscription'
                  ? handleSubscriptionPurchase
                  : () => handlePurchase()
              }
              disabled={isLoading}
              style={{ width: '100%' }}
            >
              {isLoading
                ? t('processing')
                : activeTab === 'subscription'
                  ? selectedPlan.type === 'yearly'
                    ? t('get_full_access_yearly', {
                        price: selectedPlan.displayPrice,
                      })
                    : t('get_full_access_weekly', {
                        price: selectedPlan.displayPrice,
                      })
                  : `${t('buy_now')} $ ${selectedOption.price}`}
            </BlueButton>
          ) : (
            <PrimaryButton
              onClick={
                activeTab === 'subscription'
                  ? handleSubscriptionPurchase
                  : () => handlePurchase()
              }
              disabled={isLoading}
              style={{ width: '100%' }}
            >
              {isLoading
                ? t('processing')
                : activeTab === 'subscription'
                  ? selectedPlan.type === 'yearly'
                    ? t('get_full_access_yearly', {
                        price: selectedPlan.displayPrice,
                      })
                    : t('get_full_access_weekly', {
                        price: selectedPlan.displayPrice,
                      })
                  : `${t('buy_now')} $ ${selectedOption.price}`}
            </PrimaryButton>
          )}

          <div style={styles.footer}>
            <a href="/terms" style={styles.footerLink}>
              <FontText small style={styles.footerLink}>
                {t('terms_of_service')}
              </FontText>
            </a>
            <a href="/privacy" style={styles.footerLink}>
              <FontText tiny style={styles.footerLink}>
                {t('privacy_policy')}
              </FontText>
            </a>
          </div>

          {isLoading && (
            <div style={styles.loader}>
              <div style={styles.spinner} />
              <FontText style={styles.loaderText}>
                {t('processing_purchase')}
              </FontText>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
